import { useRef, useState } from 'react';
import type { MouseEvent, PointerEvent } from 'react';

const INDEX_ATTRIBUTE = 'data-index';

const PRIMARY_BUTTON = 0;

/** The index of the cell under a point, whatever element the pointer happens to be captured by. */
const indexUnder = (clientX: number, clientY: number): number | null => {
  const cell = document.elementFromPoint(clientX, clientY)?.closest(`[${INDEX_ATTRIBUTE}]`);
  const raw = cell?.getAttribute(INDEX_ATTRIBUTE);

  if (raw === null || raw === undefined) {
    return null;
  }

  const index = Number(raw);

  return Number.isInteger(index) ? index : null;
};

const rangeOf = (from: number, to: number): Set<number> => {
  const low = Math.min(from, to);
  const size = Math.abs(to - from) + 1;

  return new Set(Array.from({ length: size }, (_unused, offset) => low + offset));
};

const toggled = (selected: Set<number>, index: number): Set<number> => {
  const next = new Set(selected);

  if (next.has(index)) {
    next.delete(index);
    return next;
  }

  next.add(index);
  return next;
};

type GridHandlers = {
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
};

export type RangeSelection = {
  selected: Set<number>;
  gridHandlers: GridHandlers;
  /** For the keyboard: a tap is already handled by the grid, so a pointer click is ignored here. */
  onCellClick: (index: number, event: MouseEvent<HTMLButtonElement>) => void;
  selectAll: (count: number) => void;
  clear: () => void;
};

/**
 * A tap adds a cell to the selection or takes it out; a drag across cells selects exactly the
 * stretch between where it started and where the finger is. The grid captures the pointer, so a
 * drag keeps being followed when the finger leaves the cell it started on - and the cell under it
 * is found by position, because a captured pointer reports the capturing element as its target.
 */
export const useRangeSelection = (): RangeSelection => {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const anchor = useRef<number | null>(null);
  const hasDragged = useRef(false);

  const onPointerDown = (event: PointerEvent<HTMLElement>): void => {
    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    const index = indexUnder(event.clientX, event.clientY);

    if (index === null) {
      return;
    }

    anchor.current = index;
    hasDragged.current = false;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
    const start = anchor.current;

    if (start === null) {
      return;
    }

    const index = indexUnder(event.clientX, event.clientY);
    const isStillOnStart = index === start && !hasDragged.current;

    if (index === null || isStillOnStart) {
      return;
    }

    hasDragged.current = true;
    setSelected(rangeOf(start, index));
  };

  const onPointerUp = (): void => {
    const start = anchor.current;

    anchor.current = null;

    if (start === null || hasDragged.current) {
      return;
    }

    setSelected((current) => toggled(current, start));
  };

  const onPointerCancel = (): void => {
    anchor.current = null;
  };

  const onCellClick = (index: number, event: MouseEvent<HTMLButtonElement>): void => {
    const isKeyboardActivation = event.detail === 0;

    if (!isKeyboardActivation) {
      return;
    }

    setSelected((current) => toggled(current, index));
  };

  return {
    selected,
    gridHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    onCellClick,
    selectAll: (count) => {
      setSelected(rangeOf(0, count - 1));
    },
    clear: () => {
      setSelected(new Set());
    },
  };
};
